<template>
  <div
    :class="[
      'inline-flex items-center justify-center font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-full',
      sizeClasses,
    ]"
    :style="{ backgroundColor: customColor }"
  >
    <img
      v-if="avatarUrl"
      :src="avatarUrl"
      :alt="alt"
      :class="['rounded-full object-cover', sizeClasses]"
      @error="onImageError"
    />
    <span v-else :class="textSizeClasses">
      {{ userInitials }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useUserAvatar } from '../composables/useUserProfile';

  interface Props {
    email?: string;
    avatarUrl?: string | null;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    customColor?: string;
    alt?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    alt: 'User avatar',
  });

  const { userInitials: defaultInitials, generateInitials } = useUserAvatar();
  const imageError = ref(false);

  // Compute initials based on provided email or default user
  const userInitials = computed(() => {
    if (props.email) {
      return generateInitials(props.email);
    }
    return defaultInitials.value;
  });

  // Size classes for different avatar sizes
  const sizeClasses = computed(() => {
    const sizes = {
      xs: 'w-6 h-6',
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-16 h-16',
      '2xl': 'w-20 h-20',
    };
    return sizes[props.size];
  });

  // Text size classes for initials
  const textSizeClasses = computed(() => {
    const sizes = {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
    };
    return sizes[props.size];
  });

  // Show avatar URL only if provided and no error occurred
  const avatarUrl = computed(() => {
    return !imageError.value ? props.avatarUrl : null;
  });

  const onImageError = () => {
    imageError.value = true;
  };
</script>
